import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import BillingPageFrame from "./BillingPageFrame";
import BuyCreditsDialog from "./BuyCreditsDialog";
import { listTopups } from "./billingApi";
import { formatCreditCount, formatRolesForDisplay, formatRupeesFromPaisa, formatWhen } from "./billingFormat";
import { useBilling } from "../../context/BillingContext";

const STATUS_COLOR = {
    SUCCESS: "success",
    PENDING: "warning",
    FAILED: "error",
};

const CreditTopupsPage = () => {
    const { isCreditBased, canManageBilling, refresh, loading: billingLoading } = useBilling();
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [lastPage, setLastPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [buyOpen, setBuyOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const result = await listTopups({ page, per_page: 20 });
            setRows(result.rows);
            setLastPage(result.lastPage);
        } catch (err) {
            setRows([]);
            setError(err?.status === 403
                ? "Only the account admin can view top-ups."
                : (err?.message || "Could not load top-ups"));
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        if (billingLoading || !isCreditBased) {
            if (!billingLoading) setLoading(false);
            return;
        }
        load();
    }, [billingLoading, isCreditBased, load]);

    const handlePaid = async () => {
        await refresh();
        if (page === 1) {
            await load();
        } else {
            setPage(1);
        }
    };

    return (
        <BillingPageFrame
            title="Top-ups"
            subtitle="Payments that added credits. Online checkouts and credits added by the system are listed here."
            maxWidth="100%"
        >
            {!billingLoading && !isCreditBased ? (
                <Alert severity="info">Top-ups are available on credit accounts. Your balance is under Credits Information.</Alert>
            ) : (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            flexWrap: "wrap",
                            mb: 2,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Buy credits here. Successful payments appear in this list.
                        </Typography>
                        {canManageBilling && (
                            <Button variant="contained" onClick={() => setBuyOpen(true)}>
                                Top up
                            </Button>
                        )}
                    </Box>
                    {!canManageBilling && !billingLoading && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Only the account admin can buy credits. You can still view the top-up history for this wallet.
                        </Alert>
                    )}
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} /></Box>
                    ) : rows.length === 0 ? (
                        <Alert severity="info">
                            No top-ups yet.
                            {canManageBilling ? " Use Top up to buy credits." : " Credits bought for this account will appear here."}
                        </Alert>
                    ) : (
                        <Box sx={{ width: "100%", overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 2 }}>
                            <Table size="small" sx={{ width: "100%", minWidth: 640 }}>
                                <TableHead>
                                    <TableRow>
                                        {["When", "Amount", "Mode", "Status", "Reference"].map((heading) => (
                                            <TableCell key={heading} sx={{ fontWeight: 700 }}>{heading}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => {
                                        const roles = formatRolesForDisplay(row.user_role || row.user?.user_role);
                                        return (
                                        <TableRow key={row.id}>
                                            <TableCell>{formatWhen(row.created_at || row.updated_at)}</TableCell>
                                            <TableCell>
                                                {formatRupeesFromPaisa(row.credit_amount)}
                                                <Box component="span" sx={{ display: "block", color: "text.secondary", fontSize: "0.75rem" }}>
                                                    {formatCreditCount(row.credit_amount)}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {row.payment_mode === "ADMIN" ? "Added by system" : "Online payment"}
                                                {roles && (
                                                    <Box component="span" sx={{ display: "block", color: "text.secondary", fontSize: "0.75rem" }}>
                                                        {roles}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.payment_status || "—"}
                                                    color={STATUS_COLOR[row.payment_status] || "default"}
                                                />
                                            </TableCell>
                                            <TableCell>{row.payment_gateway_id || "—"}</TableCell>
                                        </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                    {lastPage > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <Pagination count={lastPage} page={page} onChange={(_, value) => setPage(value)} />
                        </Box>
                    )}
                    <BuyCreditsDialog
                        open={buyOpen}
                        onClose={() => setBuyOpen(false)}
                        onPaid={handlePaid}
                    />
                </>
            )}
        </BillingPageFrame>
    );
};

export default CreditTopupsPage;
